import React, { useState, useRef } from 'react';
import { X, Upload, FileText, AlertCircle, CheckCircle, Loader } from 'lucide-react';
import { resumeService } from '../../../services/resumeService';

const ResumeUploadModal = ({ isOpen, onClose, candidateId, onUploadSuccess }) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadStatus, setUploadStatus] = useState('idle'); // idle, uploading, success, error
  const [uploadMessage, setUploadMessage] = useState('');
  const fileInputRef = useRef(null);

  const allowedTypes = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  ];
  
  const maxFileSize = 10 * 1024 * 1024; // 10MB

  if (!isOpen) return null;

  const handleDragOver = (e) => {
    e?.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e?.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setIsDragOver(false);
    
    const files = Array.from(e?.dataTransfer?.files || []);
    if (files?.length > 0) {
      handleFileSelect(files?.[0]);
    }
  };

  const handleFileInputChange = (e) => {
    const files = Array.from(e?.target?.files || []);
    if (files?.length > 0) {
      handleFileSelect(files?.[0]);
    }
  };

  const handleFileSelect = (file) => {
    // Validate file type
    if (!allowedTypes?.includes(file?.type)) {
      setUploadMessage('Please select a PDF, DOC, or DOCX file');
      setUploadStatus('error');
      return;
    }

    // Validate file size
    if (file?.size > maxFileSize) {
      setUploadMessage('File size must be less than 10MB');
      setUploadStatus('error');
      return;
    }

    setSelectedFile(file);
    setUploadStatus('idle');
    setUploadMessage('');
  };

  const handleUpload = async () => {
    // Enhanced validation with detailed error messages
    if (!selectedFile) {
      setUploadMessage('Please select a file to upload');
      setUploadStatus('error');
      return;
    }

    // Handle missing candidateId with better fallback logic
    let effectiveCandidateId = candidateId;
    
    if (!candidateId) {
      // Try to get candidateId from URL if not passed as prop
      const urlParts = window.location?.pathname?.split('/');
      const candidateIdFromUrl = urlParts?.[urlParts?.indexOf('candidate') + 1];
      
      if (candidateIdFromUrl && candidateIdFromUrl !== 'undefined' && candidateIdFromUrl !== 'null') {
        effectiveCandidateId = candidateIdFromUrl;
      } else {
        // If still no valid candidateId, create a temporary placeholder
        effectiveCandidateId = `temp-candidate-${Date.now()}`;
        console.warn('No candidate ID provided, using temporary ID:', effectiveCandidateId);
      }
    }

    // Validate that we have both file and candidateId
    if (!effectiveCandidateId) {
      setUploadMessage('Unable to determine candidate ID. Please try refreshing the page.');
      setUploadStatus('error');
      return;
    }

    setUploadStatus('uploading');
    setUploadMessage('Uploading and analyzing resume...');

    try {
      const result = await resumeService?.uploadAndParseResume(selectedFile, effectiveCandidateId);
      
      if (result?.success) {
        setUploadStatus('success');
        setUploadMessage(result?.message || 'Resume uploaded and parsing started successfully');
        onUploadSuccess?.();
        
        // Auto-close after success
        setTimeout(() => {
          handleClose();
        }, 2000);
      } else {
        setUploadStatus('error');
        setUploadMessage(result?.message || 'Failed to upload resume. Please try again.');
      }
    } catch (error) {
      console.error('Resume upload error:', error);
      setUploadStatus('error');
      
      // Provide more specific error messages
      if (error?.message?.includes('storage')) {
        setUploadMessage('File storage error. Please check your connection and try again.');
      } else if (error?.message?.includes('auth')) {
        setUploadMessage('Authentication error. Please refresh the page and try again.');
      } else {
        setUploadMessage('An unexpected error occurred. Please try again or contact support.');
      }
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setUploadStatus('idle');
    setUploadMessage('');
    setIsDragOver(false);
    onClose?.();
  };

  const getFileIcon = (fileName) => {
    const extension = fileName?.toLowerCase()?.split('.')?.pop();
    return <FileText className="h-8 w-8 text-blue-500" />;
  };

  const getStatusIcon = () => {
    switch (uploadStatus) {
      case 'uploading':
        return <Loader className="h-5 w-5 text-blue-500 animate-spin" />;
      case 'success':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'error':
        return <AlertCircle className="h-5 w-5 text-red-500" />;
      default:
        return null;
    }
  };

  const getStatusColor = () => {
    switch (uploadStatus) {
      case 'uploading':
        return 'text-blue-600';
      case 'success':
        return 'text-green-600';
      case 'error':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            Upload Resume
          </h3>
          <button
            onClick={handleClose}
            disabled={uploadStatus === 'uploading'}
            className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* File Upload Area */}
          <div
            className={`
              border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer
              ${isDragOver 
                ? 'border-blue-500 bg-blue-50' 
                : selectedFile 
                  ? 'border-green-500 bg-green-50' :'border-gray-300 hover:border-gray-400'
              }
              ${uploadStatus === 'uploading' ? 'opacity-50 cursor-not-allowed' : ''}
            `}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => uploadStatus !== 'uploading' && fileInputRef?.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.doc,.docx"
              onChange={handleFileInputChange}
              className="hidden"
              disabled={uploadStatus === 'uploading'}
            />

            {selectedFile ? (
              <div className="space-y-2">
                {getFileIcon(selectedFile?.name)}
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {selectedFile?.name}
                  </p>
                  <p className="text-xs text-gray-500">
                    {(selectedFile?.size / (1024 * 1024))?.toFixed(2)} MB
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <Upload className="h-8 w-8 text-gray-400 mx-auto" />
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    Drop your resume here, or click to browse
                  </p>
                  <p className="text-xs text-gray-500">
                    PDF, DOC, or DOCX up to 10MB
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Status Message */}
          {uploadMessage && (
            <div className={`mt-4 flex items-center space-x-2 ${getStatusColor()}`}>
              {getStatusIcon()}
              <p className="text-sm">{uploadMessage}</p>
            </div>
          )}

          {/* AI Processing Info */}
          {uploadStatus === 'uploading' && (
            <div className="mt-4 p-3 bg-blue-50 rounded-lg">
              <p className="text-xs text-blue-800">
                🤖 AI is analyzing the resume to extract skills, experience, and seniority level. 
                This may take a few moments...
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
          <button
            onClick={handleClose}
            disabled={uploadStatus === 'uploading'}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleUpload}
            disabled={!selectedFile || uploadStatus === 'uploading' || uploadStatus === 'success'}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {uploadStatus === 'uploading' ? 'Processing...' : 'Upload & Analyze'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResumeUploadModal;